import { ref } from 'vue'
import { i18n } from '@/i18n'

/**
 * One place that says "you may not do that".
 *
 * Until now a refusal reached the operator in one of three ways, none of them
 * good: the button was simply absent (so the question "why can't I?" had no
 * answer on screen), the write failed with a raw string like
 * `PATCH /events failed: 403 {"code":"42501"}`, or nothing happened at all.
 *
 * So: every refused write ends here, and here it is phrased — what was refused,
 * on what, and what to do about it. The modal is mounted once in App.vue and
 * reads this shared state.
 *
 * It is deliberately a modal and not a toast: a refusal is the end of what the
 * operator was doing, and it usually needs an action from somebody else (an
 * admin granting the right). A toast that fades after four seconds asks them to
 * remember a sentence they did not expect to read.
 */

export type DeniedAction = 'read' | 'create' | 'edit' | 'delete'

export interface AccessDeniedInfo {
  /** Which kind of action was refused. */
  action: DeniedAction
  /** What it was attempted on, already in the operator's language. */
  subject?: string
  /** The permission key, for the admin who has to grant it. */
  pageKey?: string
  /** The raw server message — shown in a details line, never as the headline. */
  detail?: string
}

const open = ref(false)
const info = ref<AccessDeniedInfo | null>(null)

/** Show the refusal. Repeated calls while open keep the FIRST one. */
export function denyAccess(next: AccessDeniedInfo): void {
  // A single click can trigger several writes (a chain, a fan-out); the first
  // refusal is the one that explains the rest, and three stacked dialogs about
  // the same click explain nothing.
  if (open.value) return
  info.value = next
  open.value = true
}

export function closeAccessDenied(): void {
  open.value = false
  info.value = null
}

/**
 * Is this error a permission refusal rather than a bug or an outage?
 *
 * Both back ends speak here:
 *   - PostgREST answers 401/403 with Postgres' own `42501` (insufficient
 *     privilege) or PGRST301 when the JWT is not accepted.
 *   - The Node backend answers 403 from `requirePermission`.
 *
 * A 404 is NOT treated as a refusal even though PostgREST can answer 404 for a
 * row that a policy hides: calling a missing endpoint "no permission" would
 * send people asking admins for rights that would not help.
 */
export function isPermissionError(err: unknown): boolean {
  const text = String((err as any)?.message || err || '')
  if (!text) return false
  if (/\b(401|403)\b/.test(text)) return true
  return /42501|PGRST301|permission denied|insufficient privilege|FORBIDDEN/i.test(text)
}

/**
 * Wrap a write so a refusal explains itself.
 *
 * Returns `null` when the action was refused (the modal is up); re-throws
 * anything else, because a network error or a constraint violation is not a
 * permission problem and must not be dressed up as one.
 */
export async function withAccessDenied<T>(
  fn: () => Promise<T>,
  info: AccessDeniedInfo,
): Promise<T | null> {
  try {
    return await fn()
  } catch (err) {
    if (!isPermissionError(err)) throw err
    denyAccess({ ...info, detail: String((err as any)?.message || err) })
    return null
  }
}

export function useAccessDenied() {
  return { open, info, denyAccess, closeAccessDenied, isPermissionError, withAccessDenied }
}

/** The action word, for the modal and for tests. */
export function actionLabel(action: DeniedAction): string {
  const key = `accessDenied.action.${action}`
  return i18n.global.t(key) as string
}
